import { flaggedAreaListQuerySchema, type FlaggedAreaListQuery, type Page } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useListQuery } from '../../../../../shared/hooks/use-list-query';
import { toListParams } from '../../../../../shared/policies/list-params';
import { ApiError } from '../../../../../shared/shared.api';
import { listFlaggedAreas, type FlaggedArea } from '../flagged-areas.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type FlaggedAreaListState =
  { status: 'loading' } | { status: 'loaded'; page: Page<FlaggedArea>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: FlaggedAreaListQuery) => toListParams(query).toString();

function toErrorState(err: unknown): FlaggedAreaListState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The page of flagged areas the URL's list query asks for, re-fetched whenever that query changes. */
export function useFlaggedAreaList() {
  const list = useListQuery(flaggedAreaListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<FlaggedAreaListState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    listFlaggedAreas(query)
      .then((page) => {
        if (!cancelled) setState({ status: 'loaded', page, queryKey: keyOf(query) });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);

  /** Re-fetches the current page after a mutation commits. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', page: await listFlaggedAreas(query), queryKey: keyOf(query) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, list, isRefreshing, reload };
}
